import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { AgentApi } from "apis/AgentApi";
import { toast } from "react-toastify";

const SESSION_KEY = "agent_session_id";
const USER_ID_KEY = "agent_user_id";

/* Decode userId from stored JWT token if not explicitly saved */
const getUserId = () => {
  const explicit = localStorage.getItem(USER_ID_KEY);
  if (explicit) return explicit;
  try {
    const token = localStorage.getItem("token") ?? "";
    const payload = token.replace("Bearer ", "").split(".")[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.sub ?? decoded.id ?? decoded.userId ?? null;
  } catch {
    return null;
  }
};

export default function ChatWidget() {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem(SESSION_KEY) ?? null
  );
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  /* On chat icon click: create session if not present, then open */
  const handleOpen = async () => {
    if (open) { setOpen(false); return; }

    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      const userId = getUserId();
      if (!userId) {
        toast("Could not determine user ID. Please re-login.", { type: "error" });
        return;
      }
      try {
        setLoading(true);
        const res = await AgentApi.createSession(userId);
        sid = res.data?.sessionId ?? res.data?.id ?? res.data?.session_id;
        if (!sid) throw new Error("No sessionId in response");
        localStorage.setItem(SESSION_KEY, sid);
        localStorage.setItem(USER_ID_KEY, userId);
        setSessionId(sid);
        setMessages([]);
      } catch (err) {
        toast(err?.response?.data?.message ?? "Failed to start session.", { type: "error" });
        return;
      } finally {
        setLoading(false);
      }
    }

    setOpen(true);
  };

  /* Start a brand-new session (clears existing one) */
  const handleNewSession = async () => {
    localStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setMessages([]);
    const userId = getUserId();
    if (!userId) {
      toast("Could not determine user ID. Please re-login.", { type: "error" });
      return;
    }
    try {
      setLoading(true);
      const res = await AgentApi.createSession(userId);
      const sid = res.data?.sessionId ?? res.data?.id ?? res.data?.session_id;
      if (!sid) throw new Error("No sessionId in response");
      localStorage.setItem(SESSION_KEY, sid);
      setSessionId(sid);
    } catch (err) {
      toast(err?.response?.data?.message ?? "Failed to start new session.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userId = getUserId();
    const userMsg = { role: "user", text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await AgentApi.sendChat({
        userId,
        sessionId,
        message: text,
      });
      const reply =
        res.data?.message ??
        res.data?.reply ??
        res.data?.response ??
        "...";
      setMessages((prev) => [...prev, { role: "agent", text: reply }]);
    } catch (err) {
      toast(err?.response?.data?.message ?? "Failed to send message.", { type: "error" });
      setMessages((prev) => [...prev, { role: "agent", text: "Something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return ReactDOM.createPortal(
    <>
      {/* Floating chat button — bottom-left, portal-rendered so it's scroll-independent */}
      <button
        onClick={handleOpen}
        disabled={loading && !open}
        className="fixed z-50 flex items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95"
        style={{
          bottom: "1.75rem",
          right: "1.75rem",
          width: "56px",
          height: "56px",
          background: open
            ? "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)"
            : "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
          color: "#fff",
          boxShadow: "0 6px 24px rgba(79,70,229,0.45)",
        }}
        title="AI Assistant"
      >
        {loading && !open ? (
          <i className="fas fa-spinner fa-spin text-xl" />
        ) : open ? (
          <i className="fas fa-times text-xl" />
        ) : (
          <i className="fas fa-comment-dots text-xl" />
        )}
        {sessionId && !open && (
          <span
            className="absolute top-1 right-1 w-3 h-3 rounded-full"
            style={{ background: "#22c55e", border: "2px solid #fff" }}
          />
        )}
      </button>

      {/* Chat modal */}
      {open && (
        <div
          className="fixed z-50 flex flex-col rounded-2xl overflow-hidden"
          style={{
            bottom: "5.5rem",
            right: "1.75rem",
            width: "360px",
            height: "480px",
            background: "#fff",
            boxShadow: "0 8px 40px rgba(79,70,229,0.18)",
            border: "1px solid #e5e7eb",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
            }}
          >
            <div className="flex items-center gap-2">
              <i className="fas fa-robot text-white text-sm" />
              <span className="text-white text-sm font-semibold">AI Assistant</span>
              {sessionId && (
                <span className="text-xs text-indigo-200 font-mono truncate" style={{ maxWidth: 80 }}>
                  #{sessionId.slice(-6)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleNewSession}
                disabled={loading}
                title="New Session"
                className="text-indigo-200 hover:text-white transition-colors text-xs flex items-center gap-1"
              >
                <i className="fas fa-plus-circle" />
                New
              </button>
              <button
                onClick={() => setOpen(false)}
                className="text-indigo-200 hover:text-white transition-colors"
              >
                <i className="fas fa-times text-sm" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ background: "#F8FAFC" }}>
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center opacity-50">
                <i className="fas fa-comment-dots text-3xl mb-2" style={{ color: "#4F46E5" }} />
                <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                  Ask me anything about schemes, products, or users.
                </p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className="px-3 py-2 rounded-xl text-sm max-w-xs break-words"
                  style={
                    msg.role === "user"
                      ? {
                          background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                          color: "#fff",
                          borderBottomRightRadius: 4,
                        }
                      : {
                          background: "#fff",
                          color: "var(--color-text-primary)",
                          border: "1px solid #e5e7eb",
                          borderBottomLeftRadius: 4,
                        }
                  }
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div
                  className="px-3 py-2 rounded-xl text-sm"
                  style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    color: "var(--color-text-muted)",
                    borderBottomLeftRadius: 4,
                  }}
                >
                  <i className="fas fa-spinner fa-spin mr-1" /> Thinking...
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div
            className="flex items-center gap-2 px-3 py-3"
            style={{ borderTop: "1px solid #e5e7eb", background: "#fff" }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              disabled={loading}
              className="flex-1 px-3 py-2 text-sm rounded-lg outline-none"
              style={{
                border: "1px solid var(--color-border)",
                background: "#F8FAFC",
                color: "var(--color-text-primary)",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#4F46E5")}
              onBlur={(e) => (e.target.style.borderColor = "var(--color-border)")}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="w-9 h-9 flex items-center justify-center rounded-lg transition-opacity"
              style={{
                background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                color: "#fff",
                opacity: loading || !input.trim() ? 0.5 : 1,
              }}
            >
              <i className="fas fa-paper-plane text-xs" />
            </button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
